"use client";

import { Translate } from "@/components/Translate";
import { ChevronRight } from "@/components/icons/Chevron";
import { Button } from "@/components/ui/Button";
import { Toggle, ToggleGroup } from "@/components/ui/ToggleGroup";
import { rpc } from "@/data/rpc.client";
import { ProductCardWithCampaigns } from "@/features/campaigns/ProductCardWithCampaigns";
import { ViewItemList } from "@/features/gtm";
import { ProductCardSkeleton } from "@/features/products/ProductCard";
import { useTranslations } from "@/i18n/client";
import { isMultiColorName } from "@/lib/depict/filters";
import { hexToHsl } from "@/lib/utils";
import type { BlokShoeFinder, BlokShoeFinderStep } from "@/storyblok";
import { clsx } from "@frend-digital/ui";
import { useIntersectionObserver } from "@frend-digital/ui/hooks";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useAtom, useSetAtom } from "jotai";
import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./ShoeFinderResults.module.css";
import { buildFiltersFromSelections, buildSelectionSummary } from "./filters";
import { shoeFinderSelectionsAtom, shoeFinderStartFiredAtom, shoeFinderStepAtom } from "./state";
import { trackShoeFinderComplete } from "./tracking";

const SHOE_FINDER_LIST_NAME = "shoe_finder_results";

interface ShoeFinderResultsProps {
	blok: BlokShoeFinder;
	visibleSteps: BlokShoeFinderStep[];
}

export const ShoeFinderResults = ({ blok, visibleSteps }: ShoeFinderResultsProps) => {
	const t = useTranslations();
	const setStepIndex = useSetAtom(shoeFinderStepAtom);
	const [selections, setSelections] = useAtom(shoeFinderSelectionsAtom);
	const setStartFired = useSetAtom(shoeFinderStartFiredAtom);
	const [selectedColors, setSelectedColors] = useState<string[]>([]);
	const completeFiredRef = useRef(false);
	const resultsImage = blok.resultsImage;

	const baseFilters = useMemo(
		() => buildFiltersFromSelections(visibleSteps, selections, visibleSteps.length),
		[visibleSteps, selections],
	);

	const filters = useMemo(() => {
		if (selectedColors.length === 0) return baseFilters;
		return {
			...baseFilters,
			groups: [...baseFilters.groups, { "var_color.value": selectedColors }],
		};
	}, [baseFilters, selectedColors]);

	const filtersQuery = useQuery({
		queryKey: ["shoeFinderFilters", baseFilters],
		queryFn: () => rpc.shoeFinder.getFilters(baseFilters),
	});

	const colorFilter = useMemo(
		() => filtersQuery.data?.filters.find((f) => f.field === "var_color.value"),
		[filtersQuery.data],
	);

	const productsQuery = useInfiniteQuery({
		queryKey: ["shoeFinderProducts", filters],
		queryFn: ({ pageParam }) => rpc.shoeFinder.getProducts(filters, pageParam),
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (lastPage) => {
			if (!lastPage.hasMore) return undefined;
			return lastPage.cursor;
		},
	});

	const [showMore, setShowMore] = useState(false);

	// Reset showMore when filters change
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset on filter change
	useEffect(() => {
		setShowMore(false);
	}, [filters]);

	const sizeLabel = t("Size");
	const selectionSummary = useMemo(
		() => buildSelectionSummary(visibleSteps, selections, visibleSteps.length, sizeLabel),
		[visibleSteps, selections, sizeLabel],
	);

	const selectedSize = useMemo(() => {
		for (const step of visibleSteps) {
			if (!step.isLastStep) continue;
			const selected = selections.get(step._uid);
			if (selected?.size) return [...selected][0] ?? null;
		}
		return null;
	}, [visibleSteps, selections]);

	const backToStart = () => {
		setSelections(new Map());
		setStepIndex(0);
		setStartFired(false);
	};

	const firstPageProducts = productsQuery.data?.pages[0]?.products;
	// getProducts' `total` only covers its own page (bounded by page size), so
	// it undercounts once a category has more matches than one page. getFilters
	// gathers every matching page before counting, so it reports the true total.
	const total = filtersQuery.data?.total ?? 0;

	const gtmListItems = useMemo(
		() =>
			firstPageProducts?.map((p) => ({
				item_id: p.sku,
				price: p.priceAsNumber!,
				item_name: p.name,
				quantity: 1,
			})) ?? null,
		[firstPageProducts],
	);

	useEffect(() => {
		if (completeFiredRef.current) return;
		if (!firstPageProducts) return;
		// total comes from filtersQuery now, so wait for it too — otherwise this
		// can fire once with a stale `total: 0` before filtersQuery resolves.
		if (!filtersQuery.data) return;

		const summary = visibleSteps.map((vs) => {
			const selected = Array.from(selections.get(vs._uid) ?? []);
			const labelMap = new Map(vs.answers?.map((a) => [a._uid, a.label]) ?? []);
			const selectedLabels = selected.map((uid) => labelMap.get(uid) ?? uid);
			return {
				stepUid: vs._uid,
				stepTitle: vs.title,
				selectedUids: selected,
				selectedLabels,
			};
		});

		trackShoeFinderComplete({ resultCount: total, selections: summary });
		completeFiredRef.current = true;
	}, [firstPageProducts, filtersQuery.data, total, visibleSteps, selections]);

	if (productsQuery.isLoading) {
		return (
			<div className={styles.loading}>
				{blok.loadingImage?.filename && (
					<img
						className={styles.loadingImage}
						src={blok.loadingImage.filename}
						alt={blok.loadingImage.alt ?? ""}
					/>
				)}
				<div className={styles.loadingText}>
					<h2 className={styles.loadingTitle}>{blok.loadingTitle || "Loading the right fit…"}</h2>
					{(blok.loadingDescription ||
						"We're currently loading your search results. Please hold on for a few seconds.") && (
						<p className={styles.loadingDescription}>
							{blok.loadingDescription ||
								"We're currently loading your search results. Please hold on for a few seconds."}
						</p>
					)}
				</div>
			</div>
		);
	}

	const products = productsQuery.data?.pages.flatMap((p) => p.products) ?? [];

	const title = (blok.resultsTitle || "{{count}} products").replace(
		/\{\{count\}\}/g,
		String(total),
	);

	const sizeTipTemplate =
		blok.resultsSizeTip ||
		"You have picked size {{size}}. Smaller feet tend to grow faster, up to 1,5 mm / month. Make sure to measure your child's feet and follow our size charts on the product page for the correct size.";

	const showingLabel = blok.resultsShowingLabel || "Showing results for:";

	return (
		<div className={styles.root}>
			<div className={styles.heading}>
				<h2 className={styles.title}>{title}</h2>
				{selectionSummary && (
					<p className={styles.summary}>
						<strong>{showingLabel} </strong>
						{selectionSummary}
					</p>
				)}
				{selectedSize && (
					<p className={styles.sizeTip}>
						{sizeTipTemplate.split("{{size}}").map((part, i, arr) => (
							<span key={part}>
								{part}
								{i < arr.length - 1 && <strong>{selectedSize}</strong>}
							</span>
						))}
					</p>
				)}
			</div>

			{resultsImage?.filename && (
				<img
					className={styles.resultsImage}
					src={resultsImage.filename}
					alt={resultsImage.alt ?? ""}
				/>
			)}

			{colorFilter && colorFilter.values.length > 0 && (
				<details className={styles.colorFilter}>
					<summary className={styles.colorFilterSummary}>
						<ChevronRight className={styles.colorFilterIndicator} />
						<Translate>Color</Translate>
					</summary>
					<ToggleGroup
						value={selectedColors}
						toggleMultiple
						onValueChange={setSelectedColors}
						className={styles.colorSwatches}
					>
						{colorFilter.values.map((color) => {
							const hsl = color.hex ? hexToHsl(color.hex) : null;
							const isMultiColor = isMultiColorName(color.name);

							if (isMultiColor) {
								const gradientColors = [
									"#FF0000",
									"#FF7F00",
									"#FFFF00",
									"#00FF00",
									"#0000FF",
									"#4B0082",
									"#9400D3",
								].join(", ");

								return (
									<Toggle
										key={color.value}
										value={color.value}
										circular
										className={clsx(styles.colorSwatch, styles.colorSwatchMulti)}
										style={
											{ "--gradient": `conic-gradient(${gradientColors})` } as React.CSSProperties
										}
										aria-label={color.name}
									>
										<span />
									</Toggle>
								);
							}

							return (
								<Toggle
									key={color.value}
									value={color.value}
									circular
									className={clsx(
										styles.colorSwatch,
										(hsl?.l ?? 0) > 80 && styles.colorSwatchLight,
									)}
									style={{ "--color": color.hex } as React.CSSProperties}
									aria-label={color.name}
								>
									<span />
								</Toggle>
							);
						})}
					</ToggleGroup>
				</details>
			)}

			{gtmListItems && (
				<ViewItemList
					id={SHOE_FINDER_LIST_NAME}
					name={SHOE_FINDER_LIST_NAME}
					items={gtmListItems}
				/>
			)}

			{products.length === 0 ? (
				<p className={styles.ctaText}>
					{(blok.resultsEmptyLabel as string | undefined) ||
						"No products match your selection anymore — try going back and adjusting it."}
				</p>
			) : (
				<div className={styles.productGrid}>
					{products.map((product) => (
						<ProductCardWithCampaigns
							key={product.displayItemId}
							product={product}
							sizes="(min-width: 1280px) 25vw, (min-width: 768px) 33vw, 50vw"
							itemListName={SHOE_FINDER_LIST_NAME}
						/>
					))}
					{!showMore && productsQuery.hasNextPage && (
						<div className={styles.showMoreRow}>
							<Button
								onClick={() => {
									setShowMore(true);
									productsQuery.fetchNextPage();
								}}
							>
								<Translate>Show more</Translate>
							</Button>
						</div>
					)}
					{productsQuery.isFetchingNextPage && <LoadingSkeletons />}
					{showMore && productsQuery.hasNextPage && (
						<Sentinel action={() => productsQuery.fetchNextPage()} />
					)}
				</div>
			)}

			<div className={styles.actions}>
				<Button color="black" onClick={backToStart}>
					{blok.resultsBackLabel || "Back to start"}
				</Button>
			</div>
		</div>
	);
};

const SKELETON_KEYS = ["s1", "s2", "s3", "s4"];

const LoadingSkeletons = () => (
	<>
		{SKELETON_KEYS.map((key) => (
			<ProductCardSkeleton key={key} />
		))}
	</>
);

const Sentinel = ({ action }: { action: () => void }) => {
	const [ref, entry] = useIntersectionObserver<HTMLDivElement>();

	// biome-ignore lint/correctness/useExhaustiveDependencies: trigger on intersection change
	useEffect(() => {
		if (entry?.isIntersecting) {
			action();
		}
	}, [entry?.isIntersecting]);

	return (
		<span
			ref={ref}
			style={{
				height: 1,
				opacity: 0,
				gridColumn: "1 / -1",
				marginTop: "-400px",
			}}
		/>
	);
};
